/**
 * @file ReportsHistoryPage.js
 * @description Report History screen: sortable/filterable/paginated list with row selection and actions (UI only).
 */

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { Link } from 'react-router-dom';

import '../page-list/PageList.css';

import PaginationComponent from '../helpers/PaginationComponent';
import { FilterModalV2 } from '../helpers/FilterComponent_V2';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import { getBadgeStatus } from '../helpers/getBadgeStatus';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { useTableSort, SortDirections } from '../../hooks/useTableSort';
import {
    fetchReportHistory,
    deleteGeneratedReport,
    downloadGeneratedReportFile,
} from '../../services/reportingService';
import { getStatusLabelFromReportStatusId } from './reportUtils';
import ReportViewerModal from './ReportViewerModal';
import './ReportModule.scss';
import { formatDate } from '../helpers/DateUtils';
import { getText } from '../../i18n';

const PAGE_SIZE = 10;
const STATUS_FILTER_COLUMN = 'status';
const REFRESH_INTERVAL_MS = 15000;

function buildGeneratedReportsSnapshot(generatedReports) {
    return generatedReports
        .map(generatedReport =>
            [
                generatedReport?.id,
                generatedReport?.reportStatusId,
                generatedReport?.createdAt,
                generatedReport?.createdBy,
                generatedReport?.name,
                (generatedReport?.sharedWithIndividuals || [])
                    .map(individual => individual?.id || individual?.name || '')
                    .join(','),
            ].join('|'),
        )
        .join('||');
}

function getFilteredGeneratedReports(
    generatedReports,
    searchTerm,
    statusFilters,
) {
    const normalizedTerm = String(searchTerm || '')
        .trim()
        .toLowerCase();
    const normalizedStatuses = Array.isArray(statusFilters)
        ? statusFilters.map(status => String(status).trim().toLowerCase())
        : [];

    return generatedReports.filter(generatedReport => {
        const statusLabel = getStatusLabelFromReportStatusId(
            generatedReport?.reportStatusId,
        );
        const matchesSearch =
            !normalizedTerm ||
            String(generatedReport.name || '')
                .toLowerCase()
                .includes(normalizedTerm) ||
            String(generatedReport.createdBy || '')
                .toLowerCase()
                .includes(normalizedTerm);

        const matchesStatus =
            normalizedStatuses.length === 0 ||
            normalizedStatuses.includes(
                String(statusLabel || '')
                    .trim()
                    .toLowerCase(),
            );

        return matchesSearch && matchesStatus;
    });
}

function sortGeneratedReports(generatedReports, sortConfig) {
    if (!sortConfig?.key) {
        return generatedReports;
    }

    const { key, direction } = sortConfig;
    const directionFactor = direction === SortDirections.ASCENDING ? 1 : -1;

    return [...generatedReports].sort((a, b) => {
        const aVal = a?.[key];
        const bVal = b?.[key];

        if (key === 'createdAt') {
            const aTime = aVal ? new Date(aVal).getTime() : 0;
            const bTime = bVal ? new Date(bVal).getTime() : 0;
            if (aTime === bTime) {
                return 0;
            }
            return aTime < bTime ? -1 * directionFactor : 1 * directionFactor;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            const cmp = aVal.localeCompare(bVal, undefined, {
                sensitivity: 'base',
            });
            return cmp * directionFactor;
        }

        if (aVal < bVal) {
            return -1 * directionFactor;
        }

        if (aVal > bVal) {
            return 1 * directionFactor;
        }

        return 0;
    });
}

function getReadOnlyReportDetails(generatedReport) {
    return {
        ...generatedReport,
        reportName: generatedReport?.name || '',
        fromDate: null,
        toDate: null,
        isSingleDateMode: false,
        accessibleBy: (generatedReport?.sharedWithIndividuals || [])
            .map(individual => individual?.name || '')
            .filter(Boolean),
    };
}

function AccessibleByCell({ accessibleBy, onMoreClick }) {
    const names = Array.isArray(accessibleBy) ? accessibleBy : [];
    const firstName = names[0];
    const remainder = names.slice(1);

    if (!firstName) {
        return <span>-</span>;
    }

    const pillStyle = {
        backgroundColor: 'var(--iNGAGED-Primary-Color)',
        color: 'white',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '18px',
        padding: '4px 10px',
    };

    const remainderCount = remainder.length;

    return (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span className="badge rounded-pill" style={pillStyle}>
                {firstName}
            </span>
            {remainderCount > 0 && (
                <span
                    className="reportAccessibleByMore"
                    aria-label={`plus ${remainderCount} more`}
                    role="button"
                    tabIndex={0}
                    onClick={event => onMoreClick(event, remainder)}
                    onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onMoreClick(event, remainder);
                        }
                    }}
                >
                    +{remainderCount} {getText('msg_e7c95b4c2824')}
                </span>
            )}
        </div>
    );
}

function ReportsHistoryPage() {
    const [generatedReports, setGeneratedReports] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeModal, setActiveModal] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);

    const [downloadMenuOpenId, setDownloadMenuOpenId] = useState(null);
    const [viewerGeneratedReport, setViewerGeneratedReport] = useState(null);
    const [sharedWithAnchorEl, setSharedWithAnchorEl] = useState(null);
    const [sharedWithOverflowNames, setSharedWithOverflowNames] = useState([]);

    const selectAllRef = useRef(null);
    const downloadMenuRef = useRef(null);
    const lastGeneratedReportsSnapshotRef = useRef('');
    const { showModal } = useContext(ModalContext);

    const { sortConfig, renderSortArrow } = useTableSort(
        'createdAt',
        SortDirections.DESCENDING,
    );

    const statusFilters = useMemo(
        () => selectedOptions[STATUS_FILTER_COLUMN] || [],
        [selectedOptions],
    );
    const sharedWithPopoverOpen = Boolean(sharedWithAnchorEl);
    const sharedWithPopoverId = sharedWithPopoverOpen
        ? 'report-shared-with-popover'
        : undefined;

    function handleSharedWithMoreClick(event, overflowNames) {
        event.stopPropagation();
        setSharedWithAnchorEl(event.currentTarget);
        setSharedWithOverflowNames(overflowNames);
    }

    function handleSharedWithPopoverClose() {
        setSharedWithAnchorEl(null);
        setSharedWithOverflowNames([]);
    }

    useEffect(() => {
        let isCancelled = false;

        async function refreshGeneratedReports({ showLoading = false } = {}) {
            if (showLoading) {
                setIsLoading(true);
            }

            try {
                const history = await fetchReportHistory();

                if (isCancelled) {
                    return;
                }

                const myGeneratedReports = history?.myReports || [];
                const sharedGeneratedReports = history?.sharedWithMe || [];
                const nextGeneratedReports = [
                    ...myGeneratedReports,
                    ...sharedGeneratedReports,
                ];
                const nextSnapshot =
                    buildGeneratedReportsSnapshot(nextGeneratedReports);

                if (nextSnapshot !== lastGeneratedReportsSnapshotRef.current) {
                    lastGeneratedReportsSnapshotRef.current = nextSnapshot;
                    setGeneratedReports(nextGeneratedReports);
                }
            } catch (error) {
                if (!isCancelled) {
                    if (showLoading) {
                        setGeneratedReports([]);
                        lastGeneratedReportsSnapshotRef.current = '';
                        Toast.error(getText('msg_f83e35941b64'));
                    }
                }
            } finally {
                if (!isCancelled && showLoading) {
                    setIsLoading(false);
                }
            }
        }

        refreshGeneratedReports({ showLoading: true });

        const intervalId = setInterval(() => {
            refreshGeneratedReports();
        }, REFRESH_INTERVAL_MS);

        return () => {
            isCancelled = true;
            clearInterval(intervalId);
        };
    }, []);

    const filteredGeneratedReports = useMemo(
        () =>
            getFilteredGeneratedReports(
                generatedReports,
                searchTerm,
                statusFilters,
            ),
        [generatedReports, searchTerm, statusFilters],
    );

    const sortedGeneratedReports = useMemo(
        () => sortGeneratedReports(filteredGeneratedReports, sortConfig),
        [filteredGeneratedReports, sortConfig],
    );

    const totalPages = useMemo(() => {
        const count = Math.ceil(sortedGeneratedReports.length / PAGE_SIZE);
        return count === 0 ? 1 : count;
    }, [sortedGeneratedReports.length]);

    const pagedGeneratedReports = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return sortedGeneratedReports.slice(start, start + PAGE_SIZE);
    }, [sortedGeneratedReports, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilters, sortConfig.key, sortConfig.direction]);

    useEffect(() => {
        setSelectedIds(prevSelected =>
            prevSelected.filter(id =>
                sortedGeneratedReports.some(
                    generatedReport => generatedReport.id === id,
                ),
            ),
        );
    }, [sortedGeneratedReports]);

    useEffect(() => {
        if (!selectAllRef.current) {
            return;
        }

        const pageIds = pagedGeneratedReports.map(
            generatedReport => generatedReport.id,
        );
        const selectedOnPageCount = selectedIds.filter(id =>
            pageIds.includes(id),
        ).length;

        selectAllRef.current.indeterminate =
            selectedOnPageCount > 0 && selectedOnPageCount < pageIds.length;
    }, [selectedIds, pagedGeneratedReports]);

    function handleToggleSelectAll(event) {
        const isChecked = event.target.checked;
        const pageIds = pagedGeneratedReports.map(
            generatedReport => generatedReport.id,
        );

        setSelectedIds(prev => {
            const remaining = prev.filter(id => !pageIds.includes(id));
            return isChecked ? [...remaining, ...pageIds] : remaining;
        });
    }

    function handleToggleRow(id) {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
        );
    }

    async function handleDeleteReports() {
        if (isDeleting) {
            return;
        }

        if (selectedIds.length === 0) {
            return;
        }

        setIsDeleting(true);

        const idsToDelete = [...selectedIds];

        try {
            await Promise.all(idsToDelete.map(id => deleteGeneratedReport(id)));

            setGeneratedReports(prevGeneratedReports =>
                prevGeneratedReports.filter(
                    generatedReport =>
                        !idsToDelete.includes(generatedReport.id),
                ),
            );
            setSelectedIds([]);
            Toast.success(
                idsToDelete.length === 1
                    ? 'Report entry deleted.'
                    : 'Report entries deleted.',
            );
        } catch (error) {
            Toast.error(getText('msg_b873f3c6338b'));
        } finally {
            setIsDeleting(false);
        }
    }

    useEffect(() => {
        function handleOutsideClick(event) {
            if (
                downloadMenuRef.current &&
                !downloadMenuRef.current.contains(event.target)
            ) {
                setDownloadMenuOpenId(null);
            }
        }

        document.addEventListener('mousedown', handleOutsideClick);
        return () =>
            document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    async function handleDownloadReport(generatedReport, formatCode) {
        setDownloadMenuOpenId(null);
        try {
            await downloadGeneratedReportFile(generatedReport.id, formatCode, {
                reportName: generatedReport.name,
                createdAt: generatedReport.createdAt,
            });
        } catch (error) {
            Toast.error(getText('msg_978096149fa8'));
        }
    }

    function openDeleteConfirmationModal() {
        if (selectedIds.length === 0) {
            Toast.error(getText('msg_84a53da7c978'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_09ab8a125055'),
            text: getText('msg_10d560aa85ab'),
            onYes: handleDeleteReports,
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
    }

    function openModal(column) {
        setActiveModal(column);
    }

    function closeModal() {
        setActiveModal(null);
    }

    function handleApplyFilter(column, values) {
        setSelectedOptions(prev => ({
            ...prev,
            [column]: values,
        }));
    }

    function renderStatusFilterModal() {
        const isSelected = Boolean(
            selectedOptions[STATUS_FILTER_COLUMN] &&
            selectedOptions[STATUS_FILTER_COLUMN].length > 0,
        );

        return (
            <>
                <span
                    onClick={() => openModal(STATUS_FILTER_COLUMN)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            openModal(STATUS_FILTER_COLUMN);
                        }
                    }}
                >
                    {isSelected ? (
                        <svg
                            style={{
                                marginTop: '4px',
                                display: 'inline-block',
                                width: '1em',
                                height: '1em',
                            }}
                        >
                            <path
                                d="M1.08871 2.81875C0.701376 2.38585 0.507709 2.1694 0.500404 1.98544C0.494058 1.82564 0.562731 1.67205 0.686053 1.57022C0.828016 1.453 1.11846 1.453 1.69935 1.453H9.30065C9.88154 1.453 10.172 1.453 10.3139 1.57022C10.4373 1.67205 10.5059 1.82564 10.4996 1.98544C10.4923 2.1694 10.2986 2.38585 9.91129 2.81875L6.98903 6.0848C6.91182 6.17109 6.87322 6.21424 6.84569 6.26335C6.82128 6.3069 6.80336 6.35378 6.79252 6.40252C6.78029 6.45747 6.78029 6.51537 6.78029 6.63116V9.36948C6.78029 9.46963 6.78029 9.5197 6.76414 9.563C6.74986 9.60125 6.72665 9.63554 6.69643 9.663C6.66223 9.69408 6.61574 9.71268 6.52275 9.74987L4.78156 10.4463C4.59333 10.5216 4.49922 10.5593 4.42367 10.5436C4.35761 10.5299 4.29963 10.4906 4.26235 10.4344C4.21971 10.37 4.21971 10.2687 4.21971 10.066V6.63116C4.21971 6.51537 4.21971 6.45747 4.20748 6.40252C4.19664 6.35378 4.17872 6.3069 4.15431 6.26335C4.12678 6.21424 4.08818 6.17109 4.01096 6.0848L1.08871 2.81875Z"
                                fill="#1366FF"
                                stroke="#1366FF"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ) : (
                        <i
                            className="bi bi-funnel"
                            style={{ fontSize: '0.7em' }}
                            aria-label={getText('msg_61b933ebc0cf')}
                        />
                    )}
                </span>
                {activeModal === STATUS_FILTER_COLUMN && (
                    <FilterModalV2
                        column={STATUS_FILTER_COLUMN}
                        options={Array.from(
                            new Set(
                                generatedReports.map(generatedReport =>
                                    getStatusLabelFromReportStatusId(
                                        generatedReport.reportStatusId,
                                    ),
                                ),
                            ),
                        )}
                        selectedOptions={
                            selectedOptions[STATUS_FILTER_COLUMN] || []
                        }
                        onApply={handleApplyFilter}
                        onClose={closeModal}
                    />
                )}
            </>
        );
    }

    const isDeleteDisabled = selectedIds.length === 0 || isDeleting;

    return (
        <>
            <div className="page-content">
                <div className="card">
                    <div className="card-body px-0 py-4">
                        <div className="d-flex align-items-center mb-4 event-log-header-row">
                            <div className="event-log-header-container flex-grow-1">
                                <p className="page-title float-start card-title ms-4 m-0">
                                    {getText('msg_6f0202f58069')}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_1829fddced01')}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="ing-text-buttons"
                                style={{
                                    margin: '2px 20px 0 0',
                                    alignSelf: 'flex-start',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    border: 'none',
                                    background: 'transparent',
                                    opacity: isDeleteDisabled ? 0.5 : 1,
                                    cursor: isDeleteDisabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                }}
                                onClick={openDeleteConfirmationModal}
                                disabled={isDeleteDisabled}
                            >
                                <i className="bi bi-trash" />
                                {getText('msg_f6fdbe48dc54')}
                            </button>
                        </div>

                        <div className="d-flex align-items-center px-4 pb-4 gap-3">
                            <div className="form-group col-12 col-lg-6 position-relative mb-0">
                                <input
                                    id="reportHistorySearch"
                                    type="text"
                                    maxLength="100"
                                    className="form-control pe-5"
                                    value={searchTerm}
                                    onChange={event =>
                                        setSearchTerm(event.target.value)
                                    }
                                    name="reportHistorySearch"
                                    placeholder={getText('msg_e74ba633e86f')}
                                />
                                <i
                                    className="bi bi-search position-absolute"
                                    style={{
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'var(--iNGAGED-AccessoryForeground-Color1)',
                                        pointerEvents: 'none',
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ minHeight: '70vh' }}>
                            {isLoading && (
                                <Spinner
                                    width={50}
                                    className="pageListloadingSpinner"
                                />
                            )}

                            {!isLoading && pagedGeneratedReports.length > 0 && (
                                <div className="table-responsive-xxl w-100">
                                    <table className="table table-hover align-middle">
                                        <thead className="custom-table-header table-light">
                                            <tr>
                                                <th
                                                    style={{
                                                        paddingLeft: '20px',
                                                        width: '1%',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <input
                                                        ref={selectAllRef}
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        onChange={
                                                            handleToggleSelectAll
                                                        }
                                                        checked={
                                                            pagedGeneratedReports.length >
                                                                0 &&
                                                            pagedGeneratedReports.every(
                                                                generatedReport =>
                                                                    selectedIds.includes(
                                                                        generatedReport.id,
                                                                    ),
                                                            )
                                                        }
                                                        aria-label={getText('msg_f52f04faf10e')}
                                                    />
                                                </th>
                                                <th>
                                                    <span className="table-column-header-text">
                                                        {getText('msg_d07e8350bf3d')}
                                                    </span>
                                                    {renderSortArrow('name')}
                                                </th>
                                                <th
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <span className="table-column-header-text">
                                                        {getText('msg_e6a0d7ebbfcf')}
                                                    </span>
                                                    {renderSortArrow(
                                                        'createdAt',
                                                    )}
                                                </th>
                                                <th
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <span
                                                        className="table-column-header-text"
                                                        style={{
                                                            marginRight: '8px',
                                                        }}
                                                    >
                                                        {getText('msg_bae7d5be7082')}
                                                    </span>
                                                    {renderStatusFilterModal()}
                                                </th>
                                                <th>
                                                    <span className="table-column-header-text">
                                                        {getText('msg_4cf8f74b6e21')}
                                                    </span>
                                                    {renderSortArrow(
                                                        'createdBy',
                                                    )}
                                                </th>
                                                <th>
                                                    <span className="table-column-header-text">
                                                        {getText('msg_adaec40b1d69')}
                                                    </span>
                                                </th>
                                                <th
                                                    style={{
                                                        width: '1%',
                                                        whiteSpace: 'nowrap',
                                                        paddingRight: '20px',
                                                    }}
                                                >
                                                    <span className="table-column-header-text">
                                                        {getText('msg_c3cd636a585b')}
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedGeneratedReports.map(
                                                generatedReport => {
                                                    const isSelected =
                                                        selectedIds.includes(
                                                            generatedReport.id,
                                                        );
                                                    return (
                                                        <tr
                                                            key={
                                                                generatedReport.id
                                                            }
                                                            className={
                                                                isSelected
                                                                    ? 'clicked-class'
                                                                    : ''
                                                            }
                                                        >
                                                            <td
                                                                style={{
                                                                    paddingLeft:
                                                                        '20px',
                                                                    width: '1%',
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={
                                                                        isSelected
                                                                    }
                                                                    onChange={() =>
                                                                        handleToggleRow(
                                                                            generatedReport.id,
                                                                        )
                                                                    }
                                                                    aria-label={`select ${generatedReport.id}`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Link
                                                                    to={ROUTE_BUILDERS.reportsView(
                                                                        generatedReport.id,
                                                                    )}
                                                                    state={{
                                                                        readOnly: true,
                                                                        fromReportHistory: true,
                                                                        reportDetails:
                                                                            getReadOnlyReportDetails(
                                                                                generatedReport,
                                                                            ),
                                                                    }}
                                                                    className="titleCell"
                                                                >
                                                                    {
                                                                        generatedReport.name
                                                                    }
                                                                </Link>
                                                            </td>
                                                            <td
                                                                className="subtitleCell"
                                                                style={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {generatedReport.createdAt
                                                                    ? formatDate(
                                                                          generatedReport.createdAt,
                                                                      )
                                                                    : '-'}
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`item-page-status badge-base ${getBadgeStatus().Class(
                                                                        getStatusLabelFromReportStatusId(
                                                                            generatedReport.reportStatusId,
                                                                        ),
                                                                    )}`}
                                                                >
                                                                    <svg
                                                                        className="me-1"
                                                                        width={
                                                                            8
                                                                        }
                                                                        height={
                                                                            8
                                                                        }
                                                                        viewBox="0 0 8 8"
                                                                        fill="none"
                                                                    >
                                                                        <circle
                                                                            cx={
                                                                                4
                                                                            }
                                                                            cy={
                                                                                4
                                                                            }
                                                                            r={
                                                                                3
                                                                            }
                                                                            fill={getBadgeStatus().SVGColor(
                                                                                getStatusLabelFromReportStatusId(
                                                                                    generatedReport.reportStatusId,
                                                                                ),
                                                                            )}
                                                                        />
                                                                    </svg>
                                                                    {getStatusLabelFromReportStatusId(
                                                                        generatedReport.reportStatusId,
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td className="subtitleCell">
                                                                {
                                                                    generatedReport.createdBy
                                                                }
                                                            </td>
                                                            <td className="subtitleCell">
                                                                <AccessibleByCell
                                                                    accessibleBy={(
                                                                        generatedReport?.sharedWithIndividuals ||
                                                                        []
                                                                    )
                                                                        .map(
                                                                            individual =>
                                                                                individual?.name ||
                                                                                '',
                                                                        )
                                                                        .filter(
                                                                            Boolean,
                                                                        )}
                                                                    onMoreClick={
                                                                        handleSharedWithMoreClick
                                                                    }
                                                                />
                                                            </td>
                                                            <td
                                                                style={{
                                                                    width: '1%',
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                    paddingRight:
                                                                        '20px',
                                                                }}
                                                            >
                                                                {getStatusLabelFromReportStatusId(
                                                                    generatedReport.reportStatusId,
                                                                ) ===
                                                                    'Completed' && (
                                                                    <div
                                                                        style={{
                                                                            display:
                                                                                'flex',
                                                                            gap: '12px',
                                                                            alignItems:
                                                                                'center',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                position:
                                                                                    'relative',
                                                                            }}
                                                                            ref={
                                                                                downloadMenuOpenId ===
                                                                                generatedReport.id
                                                                                    ? downloadMenuRef
                                                                                    : null
                                                                            }
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className="p-0 circularIconButton"
                                                                                aria-label={getText('msg_3442bd8e61e4')}
                                                                                onClick={() =>
                                                                                    setDownloadMenuOpenId(
                                                                                        prev =>
                                                                                            prev ===
                                                                                            generatedReport.id
                                                                                                ? null
                                                                                                : generatedReport.id,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i className="bi bi-cloud-download" />
                                                                            </button>
                                                                            {downloadMenuOpenId ===
                                                                                generatedReport.id && (
                                                                                <div className="report-download-menu">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="report-download-menu-item"
                                                                                        onClick={() =>
                                                                                            handleDownloadReport(
                                                                                                generatedReport,
                                                                                                'csv',
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        {getText('msg_3281188351b8')}
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="report-download-menu-item"
                                                                                        onClick={() =>
                                                                                            handleDownloadReport(
                                                                                                generatedReport,
                                                                                                'excel',
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        {getText('msg_a08e11119aef')}
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="p-0 circularIconButton"
                                                                            aria-label={getText('msg_96109ce947f8')}
                                                                            onClick={() =>
                                                                                setViewerGeneratedReport(
                                                                                    generatedReport,
                                                                                )
                                                                            }
                                                                        >
                                                                            <i className="bi bi-eye" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                },
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!isLoading &&
                                pagedGeneratedReports.length === 0 && (
                                    <NotFoundBox
                                        backgroundColor="#f0f4ff"
                                        title={getText('msg_1fd9f3cfed04')}
                                        description={getText('msg_cb1d6520e567')}
                                    />
                                )}
                        </div>

                        <div className="px-4 mt-3">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {viewerGeneratedReport && (
                <ReportViewerModal
                    generatedReportId={viewerGeneratedReport.id}
                    reportName={viewerGeneratedReport.name}
                    fileSizeBytes={viewerGeneratedReport.fileSizeBytes}
                    onClose={() => setViewerGeneratedReport(null)}
                />
            )}

            <Popover
                id={sharedWithPopoverId}
                open={sharedWithPopoverOpen}
                anchorEl={sharedWithAnchorEl}
                onClose={handleSharedWithPopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        maxWidth: '600px',
                        width: '100%',
                        padding: '16px',
                    }}
                >
                    {sharedWithOverflowNames.map(name => (
                        <span
                            key={name}
                            className="badge rounded-pill"
                            style={{
                                backgroundColor: 'var(--iNGAGED-Primary-Color)',
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '12px',
                                lineHeight: '18px',
                                padding: '4px 10px',
                            }}
                        >
                            {name}
                        </span>
                    ))}
                </Box>
            </Popover>
        </>
    );
}

export default ReportsHistoryPage;
